import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Save, Eye, LayoutTemplate, Split, Info, Loader2, GripVertical,
    RefreshCw, Code2, Bold, Italic, Underline, List, AlignLeft,
    AlignCenter, AlignRight, Type, Image as ImageIcon, UploadCloud, FileText,
    Plus, X, UserCheck, Link, Settings2, Trash2, Sparkles, AlertCircle, Mail
} from 'lucide-react';
import templateApi from '../api/templateApi';
import api from '../api/api';
import TemplatePickerModal from './TemplatePickerModal';
import { useConfirm } from '../contexts/ConfirmContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

// ─── Native Rich Text Editor (same as Templates.js) ───────────────────────────
const NativeRichEditor = ({ value, onChange, onImageUpload, isUploading }) => {
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    const execCommand = (command, val = null) => {
        document.execCommand(command, false, val);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    const handleInput = () => {
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel.getRangeAt && sel.rangeCount) window.savedRange = sel.getRangeAt(0);
    };

    const restoreSelection = () => {
        if (window.savedRange) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(window.savedRange);
        } else if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    const handleImageClick = () => {
        saveSelection();
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                const url = await onImageUpload(file);
                if (url) {
                    if (!window.savedRange && editorRef.current) {
                        editorRef.current.focus();
                        const range = document.createRange();
                        range.selectNodeContents(editorRef.current);
                        range.collapse(false);
                        const sel = window.getSelection();
                        sel.removeAllRanges();
                        sel.addRange(range);
                        window.savedRange = range;
                    }
                    restoreSelection();
                    execCommand('insertImage', url);
                }
            }
        };
        input.click();
    };

    const handleColorChange = (e) => {
        restoreSelection();
        execCommand('foreColor', e.target.value);
    };

    return (
        <div className="flex flex-col h-full bg-background border border-border/60 rounded-2xl overflow-hidden shadow-sm">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-muted/30 border-b border-border shadow-sm shrink-0">
                <button type="button" onClick={() => execCommand('bold')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Bold"><Bold className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => execCommand('italic')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Italic"><Italic className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => execCommand('underline')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Underline"><Underline className="w-3.5 h-3.5" /></button>
                <div className="w-px h-4 bg-border mx-1" />
                <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Bullet List"><List className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => execCommand('justifyLeft')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Align Left"><AlignLeft className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => execCommand('justifyCenter')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Align Center"><AlignCenter className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => execCommand('justifyRight')} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Align Right"><AlignRight className="w-3.5 h-3.5" /></button>
                <div className="w-px h-4 bg-border mx-1" />
                <div className="relative flex items-center group p-2 hover:bg-primary/10 rounded-lg transition-all">
                    <Type className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
                    <input type="color" onChange={handleColorChange} className="w-4 h-4 cursor-pointer bg-transparent border-none p-0 overflow-hidden" title="Text Color" />
                </div>
                <button type="button" onClick={handleImageClick} className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors" title="Inline Image">
                    <ImageIcon className="w-3.5 h-3.5" />
                </button>
                <div className="w-px h-4 bg-border mx-1" />
                <button 
                    type="button" 
                    onClick={() => execCommand('createLink', '{{unsubscribeUrl}}')} 
                    className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-colors flex items-center gap-1 text-[10px] font-bold"
                    title="Insert Unsubscribe Link"
                >
                    <Link className="w-3.5 h-3.5" /> Unsubscribe
                </button>
            </div>

            {/* Content-editable body */}
            <div className="relative flex-1 flex flex-col min-h-0 bg-background">
                <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleInput}
                    onBlur={saveSelection}
                    onKeyUp={saveSelection}
                    onMouseUp={saveSelection}
                    className="flex-1 p-6 overflow-y-auto focus:outline-none prose prose-zinc max-w-none text-foreground leading-7 text-sm"
                    style={{ minHeight: '360px' }}
                />
                {isUploading && (
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10 transition-all">
                        <div className="bg-card p-4 rounded-2xl border shadow-2xl flex items-center gap-3 animate-in zoom-in-95 duration-200">
                            <Loader2 className="w-4 h-4 text-primary animate-spin" />
                            <span className="text-xs font-black uppercase tracking-widest text-primary">Uploading...</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Variable chip (drag-and-drop into contentEditable) ───────────────────────
const VARIABLES = [
    { variable: 'firstName', label: 'First Name', icon: '👤' },
    { variable: 'lastName', label: 'Last Name', icon: '👤' },
    { variable: 'company', label: 'Company', icon: '🏢' },
    { variable: 'title', label: 'Job Title', icon: '👔' },
    { variable: 'email', label: 'Email', icon: '📧' },
];

const VariableChip = ({ variable, label, icon, onInsert }) => (
    <div
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', `{{${variable}}}`); e.dataTransfer.effectAllowed = 'copy'; }}
        onClick={() => onInsert(`{{${variable}}}`)}
        className="group flex items-center justify-between gap-1.5 cursor-grab active:cursor-grabbing bg-background hover:bg-primary/5 border border-border/60 hover:border-primary/30 p-2.5 rounded-xl transition-all shadow-sm active:scale-95"
        title={`Drag or click to insert {{${variable}}}`}
    >
        <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-xs grayscale group-hover:grayscale-0 transition-all">{icon}</span>
            <div className="flex flex-col truncate">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 group-hover:text-primary/60 transition-colors leading-none">{label}</span>
                <code className="text-[11px] font-mono font-bold text-foreground truncate">{`{{${variable}}}`}</code>
            </div>
        </div>
        <GripVertical className="w-3 h-3 text-muted-foreground/20 group-hover:text-primary transition-colors shrink-0" />
    </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const SequenceEmailEditorDrawer = ({ nodeData, workspaceId, onSave, onClose }) => {
    const confirm = useConfirm();

    const [localData, setLocalData] = useState({});
    const [activeVariantId, setActiveVariantId] = useState('root');
    const [activeTab, setActiveTab] = useState('edit');
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(null);
    const [senderPools, setSenderPools] = useState([]);
    const [mailAccounts, setMailAccounts] = useState([]);
    const [isBuildingPool, setIsBuildingPool] = useState(false);
    const [newPoolName, setNewPoolName] = useState('');
    const [newPoolAccountIds, setNewPoolAccountIds] = useState([]);
    const [newPoolRotationStrategy, setNewPoolRotationStrategy] = useState('ROUND_ROBIN');
    const [isCreatingPool, setIsCreatingPool] = useState(false);
    const subjectRef = useRef(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewData, setPreviewData] = useState({ resolvedSubject: '', resolvedBodyHtml: '', variablesFound: [], attachments: [] });

    // ── Fetch Mail Accounts & Pools ──
    useEffect(() => {
        api.get('/api/mail-accounts')
            .then(res => {
                const accounts = res.data.accounts || res.data.data || (Array.isArray(res.data) ? res.data : []);
                setMailAccounts(accounts.filter(a => a.active !== false));
            })
            .catch(err => console.error("Failed to fetch mail accounts", err));

        api.get('/api/sender-pools')
            .then(res => {
                setSenderPools(res.data?.data || []);
            })
            .catch(err => console.error("Failed to fetch sender pools", err));
    }, []);

    // ── Init ──
    useEffect(() => {
        const data = {
            templateId: nodeData.templateId || null,
            subjectTemplate: nodeData.subjectTemplate || '',
            bodyTemplate: nodeData.bodyTemplate || '',
            attachments: nodeData.attachments ? [...nodeData.attachments] : [],
            variants: nodeData.variants ? JSON.parse(JSON.stringify(nodeData.variants)) : [],
            sourceTemplateName: nodeData.sourceTemplateName || null,
            senderAccountId: nodeData.senderAccountId || null,
            senderPoolId: nodeData.senderPoolId || null
        };
        setLocalData(data);
        if (data.variants?.length > 0) setActiveVariantId(data.variants[0].id);
        else setActiveVariantId('root');
    }, [nodeData]);

    const resolveAccountLabel = (account) => (
        account?.fromName || account?.from || account?.user || account?.email || 'Unnamed Account'
    );

    const resolvePoolSummary = (poolId) => {
        const pool = senderPools.find((entry) => entry._id === poolId);
        if (!pool) return 'Sender pool will be resolved from saved configuration';
        const memberCount = pool.mailAccountIds?.length || 0;
        const strategy = (pool.rotationStrategy || 'ROUND_ROBIN').replace(/_/g, ' ');
        return `${pool.name} · ${memberCount} sender${memberCount === 1 ? '' : 's'} · ${strategy}`;
    };

    const toggleNewPoolAccount = (accountId) => {
        setNewPoolAccountIds((prev) => (
            prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
        ));
    };

    const handleCreateSenderPool = async () => {
        if (!newPoolName.trim()) return;
        if (newPoolAccountIds.length === 0) return;

        try {
            setIsCreatingPool(true);
            const res = await api.post('/api/sender-pools', {
                name: newPoolName.trim(),
                mailAccountIds: newPoolAccountIds,
                rotationStrategy: newPoolRotationStrategy,
                isActive: true
            });

            const createdPool = res.data?.data;
            if (!createdPool?._id) {
                throw new Error('Sender pool creation failed');
            }

            setSenderPools((prev) => [createdPool, ...prev]);
            setLocalData((prev) => ({
                ...prev,
                senderPoolId: createdPool._id,
                senderAccountId: null
            }));
            setIsBuildingPool(false);
            setNewPoolAccountIds([]);
        } catch (err) {
            console.error('Failed to create sender pool', err);
        } finally {
            setIsCreatingPool(false);
        }
    };

    // ── Current content ──
    const currentSubject = activeVariantId === 'root'
        ? (localData.subjectTemplate || '')
        : (localData.variants?.find(v => v.id === activeVariantId)?.subjectTemplate || '');

    const currentBody = activeVariantId === 'root'
        ? (localData.bodyTemplate || '')
        : (localData.variants?.find(v => v.id === activeVariantId)?.bodyTemplate || '');

    const handleContentChange = useCallback((field, value) => {
        setLocalData(prev => {
            const newData = { ...prev };
            if (activeVariantId === 'root') {
                newData[field] = value;
            } else {
                const vIndex = newData.variants?.findIndex(v => v.id === activeVariantId) ?? -1;
                if (vIndex > -1) {
                    newData.variants = [...newData.variants];
                    newData.variants[vIndex] = { ...newData.variants[vIndex], [field]: value };
                }
            }
            return newData;
        });
    }, [activeVariantId]);

    // ── Subject cursor-insert ──
    const insertIntoSubject = (text) => {
        const el = subjectRef.current;
        if (!el) { handleContentChange('subjectTemplate', currentSubject + text); return; }
        el.focus();
        const start = el.selectionStart, end = el.selectionEnd;
        const newVal = currentSubject.slice(0, start) + text + currentSubject.slice(end);
        handleContentChange('subjectTemplate', newVal);
        requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + text.length; });
    };

    // ── Rich editor insert (appends for contentEditable) ──
    const insertIntoBody = (text) => {
        handleContentChange('bodyTemplate', currentBody + text);
    };

    // ─── Attachment upload (same as Templates.js) ──────────────────────────────
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const newAttachment = {
                filename: file.name,
                url: res.data.url,
                size: file.size,
                contentType: file.type || 'application/octet-stream'
            };
            setLocalData(prev => ({ ...prev, attachments: [...(prev.attachments || []), newAttachment] }));
        } catch (err) {
            console.error('Attachment upload error:', err);
        } finally {
            setIsUploading(false);
            e.target.value = null;
        }
    };

    const removeAttachment = (idx) => {
        setLocalData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
    };

    // ─── Image upload for rich editor ─────────────────────────────────────────
    const handleImageUpload = async (file) => {
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            return res.data.url;
        } catch (err) {
            console.error('Image upload error:', err);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    // ─── Template apply ───────────────────────────────────────────────────────
    const handleApplyTemplate = async (template) => {
        const needsConfirm = !!(currentSubject || currentBody);
        if (needsConfirm) {
            const ok = await confirm('Replace Content?', 'This will replace your current subject and body with the selected template. Continue?');
            if (!ok) return;
        }
        setLocalData(prev => {
            const newData = { ...prev };
            if (activeVariantId === 'root') {
                newData.templateId = template._id;
                newData.subjectTemplate = template.subject;
                newData.bodyTemplate = template.bodyHtml;
                newData.sourceTemplateName = template.name;
                if (template.attachments?.length) newData.attachments = [...template.attachments];
            } else {
                const vIndex = newData.variants?.findIndex(v => v.id === activeVariantId) ?? -1;
                if (vIndex > -1) {
                    newData.variants = [...newData.variants];
                    newData.variants[vIndex] = {
                        ...newData.variants[vIndex],
                        templateId: template._id,
                        subjectTemplate: template.subject,
                        bodyTemplate: template.bodyHtml,
                        sourceTemplateName: template.name
                    };
                }
            }
            return newData;
        });
        setShowTemplatePicker(false);
        handlePreview(template.subject, template.bodyHtml);
    };

    // ─── Preview ──────────────────────────────────────────────────────────────
    const handlePreview = useCallback(async (subject = currentSubject, bodyHtml = currentBody) => {
        if (!subject && !bodyHtml) return;
        try {
            setPreviewing(true);
            const data = await templateApi.previewTemplate(workspaceId, {
                subject, bodyHtml, attachments: localData.attachments || []
            });
            setPreviewData(data);
        } catch {
            setPreviewData({ resolvedSubject: subject, resolvedBodyHtml: bodyHtml, variablesFound: [], attachments: [] });
        } finally {
            setPreviewing(false);
        }
    }, [currentSubject, currentBody, workspaceId, localData.attachments]);

    useEffect(() => {
        const to = setTimeout(() => handlePreview(), 1000);
        return () => clearTimeout(to);
    }, [currentSubject, currentBody, handlePreview]);

    // ─── A/B Toggle ───────────────────────────────────────────────────────────
    const handleToggleABTest = async () => {
        if (localData.variants?.length > 0) {
            const ok = await confirm('Disable A/B Testing?', 'You will lose Variant B configuration. This cannot be undone.');
            if (!ok) return;
            setLocalData(prev => ({
                ...prev,
                subjectTemplate: prev.variants[0].subjectTemplate,
                bodyTemplate: prev.variants[0].bodyTemplate,
                sourceTemplateName: prev.variants[0].sourceTemplateName,
                templateId: prev.variants[0].templateId || null,
                variants: []
            }));
            setActiveVariantId('root');
        } else {
            setLocalData(prev => ({
                ...prev,
                variants: [
                    { id: 'A', templateId: prev.templateId || null, subjectTemplate: prev.subjectTemplate || '', bodyTemplate: prev.bodyTemplate || '', sourceTemplateName: prev.sourceTemplateName, weight: 50 },
                    { id: 'B', templateId: null, subjectTemplate: '', bodyTemplate: '', sourceTemplateName: null, weight: 50 }
                ],
                templateId: null, subjectTemplate: '', bodyTemplate: '', sourceTemplateName: null
            }));
            setActiveVariantId('A');
        }
    };

    const sourceTemplateName = activeVariantId === 'root'
        ? localData.sourceTemplateName
        : localData.variants?.find(v => v.id === activeVariantId)?.sourceTemplateName;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-md z-50 flex justify-end animate-in fade-in duration-300">
            <div className="w-full max-w-7xl bg-card border-l-2 border-border/40 flex flex-col h-full shadow-[0_0_50px_rgba(0,0,0,0.1)] relative">
                
                {/* Close Button Abstracted */}
                <button 
                    onClick={onClose}
                    className="absolute -left-12 top-6 p-2.5 bg-background border-2 border-border rounded-xl shadow-xl hover:bg-destructive hover:text-white transition-all group active:scale-90"
                >
                    <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                </button>

                {/* Header */}
                <div className="flex items-center justify-between px-8 py-5 border-b bg-muted/10 shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
                            <Mail className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-black tracking-tight uppercase">Email Orchestrator</h2>
                                <Badge variant="outline" className="font-bold border-primary/20 bg-primary/5 text-primary">Node Configuration</Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest mt-0.5 opacity-60 italic flex items-center gap-1.5">
                                <Sparkles className="w-3 h-3" /> Professional Composition & Variant Management
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleToggleABTest} 
                            className={`rounded-xl h-10 px-4 font-black text-[10px] uppercase tracking-widest transition-all ${localData.variants?.length > 0 ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : ''}`}
                        >
                            <Split className="w-3.5 h-3.5 mr-2" />
                            {localData.variants?.length > 0 ? 'A/B Testing Active' : 'Start A/B Test'}
                        </Button>
                        <Button 
                            onClick={() => onSave(localData)} 
                            className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" /> Save Configuration
                        </Button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden bg-muted/5">

                    {/* Left Panel — Controls */}
                    <div className="w-72 shrink-0 border-r border-border/60 flex flex-col overflow-y-auto">
                        
                        {/* 1. Template Context */}
                        <div className="p-6 border-b border-border/40">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-4 flex items-center gap-2">
                                <LayoutTemplate className="w-3.5 h-3.5" /> Source Blueprint
                            </h3>
                            <div className="group relative">
                                <div className={`p-4 rounded-2xl border-2 transition-all ${sourceTemplateName ? 'bg-blue-600/5 border-blue-600/20' : 'bg-muted/10 border-dashed border-border/60 opacity-60'}`}>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[11px] font-black uppercase tracking-tight truncate max-w-full">
                                            {sourceTemplateName || 'Direct / Custom'}
                                        </span>
                                        <span className="text-[9px] text-muted-foreground font-bold">Content is locally managed</span>
                                    </div>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => setShowTemplatePicker(true)}
                                    className="w-full mt-3 rounded-xl h-9 text-[10px] font-black uppercase tracking-widest border border-border/60 hover:bg-background shadow-sm"
                                >
                                    Browse Library
                                </Button>
                            </div>
                        </div>

                        {/* 2. Sender Persona */}
                        <div className="p-6 border-b border-border/40">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-4 flex items-center gap-2">
                                <UserCheck className="w-3.5 h-3.5" /> Sender Intelligence
                            </h3>
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Resolution Mode</label>
                                    <div className="grid grid-cols-2 gap-2 p-1 bg-muted/30 rounded-xl border border-border/40">
                                        <button 
                                            onClick={() => setLocalData(prev => ({ ...prev, senderAccountId: null, senderPoolId: null }))}
                                            className={cn("py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all", !localData.senderAccountId && !localData.senderPoolId ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground")}
                                        >
                                            Default
                                        </button>
                                        <button 
                                            onClick={() => setLocalData(prev => ({ ...prev, senderAccountId: mailAccounts[0]?._id || null, senderPoolId: null }))}
                                            className={cn("py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all", localData.senderAccountId ? "bg-background text-amber-600 shadow-sm" : "text-muted-foreground hover:text-foreground")}
                                        >
                                            Identity
                                        </button>
                                        <button 
                                            onClick={() => setLocalData(prev => ({ ...prev, senderPoolId: senderPools[0]?._id || null, senderAccountId: null }))}
                                            className={cn("py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all col-span-2", localData.senderPoolId ? "bg-background text-indigo-600 shadow-sm" : "text-muted-foreground hover:text-foreground")}
                                        >
                                            Smart Pool
                                        </button>
                                    </div>
                                </div>

                                {localData.senderAccountId && (
                                    <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <select
                                            className="w-full h-11 bg-background border-2 border-amber-500/20 text-xs font-bold px-4 rounded-xl focus:border-amber-500 outline-none transition-all shadow-inner cursor-pointer"
                                            value={localData.senderAccountId}
                                            onChange={(e) => setLocalData(prev => ({ ...prev, senderAccountId: e.target.value }))}
                                        >
                                            {mailAccounts.map(acc => (
                                                <option key={acc._id} value={acc._id}>{acc.email || acc.name}</option>
                                            ))}
                                            {mailAccounts.length === 0 && <option disabled>No Identites Found</option>}
                                        </select>
                                    </div>
                                )}

                                {localData.senderPoolId && (
                                    <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-[9px] font-black uppercase tracking-widest text-indigo-600/70">
                                                {resolvePoolSummary(localData.senderPoolId)}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsBuildingPool((prev) => !prev);
                                                    setNewPoolName(`${nodeData.stepLabel || 'Sequence Node'} Pool`);
                                                    setNewPoolAccountIds([]);
                                                    setNewPoolRotationStrategy('ROUND_ROBIN');
                                                }}
                                                className="text-[9px] font-black uppercase tracking-widest text-primary hover:underline"
                                            >
                                                Create Pool
                                            </button>
                                        </div>
                                        <select
                                            className="w-full h-11 bg-background border-2 border-indigo-500/20 text-xs font-bold px-4 rounded-xl focus:border-indigo-500 outline-none transition-all shadow-inner cursor-pointer"
                                            value={localData.senderPoolId}
                                            onChange={(e) => setLocalData(prev => ({ ...prev, senderPoolId: e.target.value }))}
                                        >
                                            {senderPools.map(pool => (
                                                <option key={pool._id} value={pool._id}>{pool.name}</option>
                                            ))}
                                            {senderPools.length === 0 && <option disabled>No Pools Configured</option>}
                                        </select>
                                    </div>
                                )}

                                {isBuildingPool && (
                                    <div className="space-y-3 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div>
                                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pool Name</label>
                                            <input
                                                type="text"
                                                value={newPoolName}
                                                onChange={(e) => setNewPoolName(e.target.value)}
                                                className="mt-1 w-full h-10 rounded-xl border border-border/60 bg-background px-3 text-[11px] font-bold outline-none focus:border-primary"
                                                placeholder="Node Sender Pool"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Rotation Strategy</label>
                                            <select
                                                value={newPoolRotationStrategy}
                                                onChange={(e) => setNewPoolRotationStrategy(e.target.value)}
                                                className="mt-1 w-full h-10 rounded-xl border border-border/60 bg-background px-3 text-[11px] font-bold outline-none focus:border-primary"
                                            >
                                                <option value="ROUND_ROBIN">Round Robin</option>
                                                <option value="WEIGHTED">Weighted</option>
                                                <option value="RANDOM">Random</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Included Accounts</label>
                                            <div className="max-h-32 space-y-2 overflow-y-auto rounded-xl border border-border/60 bg-background p-2">
                                                {mailAccounts.map((account) => {
                                                    const checked = newPoolAccountIds.includes(account._id);
                                                    return (
                                                        <button
                                                            key={account._id}
                                                            type="button"
                                                            onClick={() => toggleNewPoolAccount(account._id)}
                                                            className={cn(
                                                                "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-all",
                                                                checked ? "border-primary bg-primary/5 text-primary" : "border-border/50 hover:border-primary/40"
                                                            )}
                                                        >
                                                            <span className="text-[11px] font-bold">{resolveAccountLabel(account)}</span>
                                                            <span className="text-[9px] font-black uppercase tracking-widest opacity-60">
                                                                {checked ? 'Selected' : 'Add'}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-[10px] text-muted-foreground">
                                                Create once, then this node will immediately switch to the new pool.
                                            </p>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={handleCreateSenderPool}
                                                disabled={isCreatingPool || !newPoolName.trim() || newPoolAccountIds.length === 0}
                                                className="rounded-xl text-[10px] font-black uppercase tracking-widest"
                                            >
                                                {isCreatingPool ? 'Creating...' : 'Save Pool'}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {!localData.senderAccountId && !localData.senderPoolId && (
                                    <div className="p-3 bg-accent/20 border-2 border-dashed border-border/40 rounded-xl text-[10px] text-muted-foreground font-medium italic leading-relaxed animate-in fade-in duration-500">
                                        Utilizes the sequence-level orchestration rules for participant assignment natively.
                                    </div>
                                )}

                                {localData.senderAccountId && (
                                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-[10px] text-amber-900">
                                        This node is pinned to {resolveAccountLabel(mailAccounts.find((acc) => acc._id === localData.senderAccountId))}.
                                    </div>
                                )}
                            </div>
                        </div>

                        {localData.variants?.length > 0 && (
                            <div className="p-6 border-b border-border/40 bg-purple-600/5">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-700/60 mb-4 flex items-center gap-2">
                                    <Split className="w-3.5 h-3.5" /> Experimentation Mode
                                </h3>
                                <div className="flex gap-2 p-1.5 bg-background/50 rounded-2xl border-2 border-purple-600/10 mb-4 hover:border-purple-600/30 transition-all">
                                    {localData.variants.map(v => (
                                        <button 
                                            key={v.id} 
                                            onClick={() => setActiveVariantId(v.id)} 
                                            className={cn(
                                                "flex-1 flex flex-col items-center py-2.5 rounded-xl transition-all",
                                                activeVariantId === v.id ? "bg-purple-600 text-white shadow-lg shadow-purple-600/20" : "hover:bg-purple-600/10 text-muted-foreground"
                                            )}
                                        >
                                            <span className="text-sm font-black">{v.id}</span>
                                            <span className="text-[8px] font-black uppercase tracking-widest opacity-60 leading-none mt-1">{v.weight}%</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="space-y-3 px-1">
                                    <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-purple-700/60">
                                        <span>Traffic Split</span>
                                        <span className="px-2 py-0.5 bg-white rounded border border-purple-200 text-purple-600 shadow-sm">{localData.variants.find(v => v.id === activeVariantId)?.weight || 50}%</span>
                                    </div>
                                    <input
                                        type="range" min="1" max="100"
                                        className="w-full accent-purple-600 cursor-pointer h-1.5 hover:h-2 transition-all"
                                        value={localData.variants.find(v => v.id === activeVariantId)?.weight || 50}
                                        onChange={(e) => {
                                            const vIndex = localData.variants.findIndex(v => v.id === activeVariantId);
                                            const newVars = [...localData.variants];
                                            const weight = parseInt(e.target.value);
                                            newVars[vIndex] = { ...newVars[vIndex], weight };
                                            if (newVars.length === 2) {
                                                const otherIdx = vIndex === 0 ? 1 : 0;
                                                newVars[otherIdx] = { ...newVars[otherIdx], weight: 100 - weight };
                                            }
                                            setLocalData(prev => ({ ...prev, variants: newVars }));
                                        }}
                                    />
                                    <p className="text-[9px] text-purple-700/60 font-medium italic leading-relaxed">
                                        Variants are selected deterministically per participant to ensure statistical integrity.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Sender Pool Intelligence Tooltip (Conditional) */}
                        {localData.senderPoolId && (
                            <div className="p-6 border-b border-border/40 bg-indigo-600/5 animate-in slide-in-from-left-4 duration-500">
                                <div className="flex items-center gap-2 mb-3">
                                    <Info className="w-3.5 h-3.5 text-indigo-600" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700/60">Pool Intelligence</span>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-2">
                                        <div className="w-1 h-1 rounded-full bg-indigo-400 mt-1.5" />
                                        <p className="text-[10px] text-muted-foreground font-medium leading-relaxed">
                                            <span className="font-bold text-indigo-500">Thread Continuity:</span> Replied threads will stick to the original sender account automatically.
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <div className="w-1 h-1 rounded-full bg-indigo-400 mt-1.5" />
                                        <p className="text-[10px] text-muted-foreground font-medium leading-relaxed">
                                            <span className="font-bold text-indigo-500">Health Awareness:</span> Inactive, cooling, or limited accounts are excluded in real-time.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. Intelligent Variables */}
                        <div className="p-6 flex-1">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-4 flex items-center gap-2">
                                <Settings2 className="w-3.5 h-3.5" /> Smart Variables
                            </h3>
                            <div className="space-y-2">
                                {VARIABLES.map((v) => (
                                    <VariableChip key={v.variable} {...v} onInsert={insertIntoBody} />
                                ))}
                            </div>
                            
                            <div className="mt-8 p-4 bg-background border-2 border-muted rounded-2xl">
                                <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-3">
                                    <AlertCircle className="w-3 h-3 text-amber-500" /> Validation Hint
                                </div>
                                <p className="text-[10px] text-muted-foreground font-medium leading-[1.6]">
                                    Unresolved variables will block sending. Ensure all contacts have mapped values.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Center area — Tabbed Views */}
                    <div className="flex-1 flex flex-col overflow-hidden relative">
                        {/* Tab Switcher Floating */}
                        <div className="flex items-center justify-center py-4 bg-background/40 backdrop-blur-md border-b border-border/40 shrink-0 gap-2">
                            <Button 
                                variant={activeTab === 'edit' ? 'default' : 'ghost'} 
                                size="sm"
                                onClick={() => setActiveTab('edit')} 
                                className={`rounded-xl h-9 px-6 font-black text-[10px] uppercase tracking-widest ${activeTab === 'edit' ? 'shadow-xl shadow-primary/20' : ''}`}
                            >
                                <Code2 className="w-3.5 h-3.5 mr-2" /> Composer
                            </Button>
                            <Button 
                                variant={activeTab === 'preview' ? 'default' : 'ghost'} 
                                size="sm"
                                onClick={() => setActiveTab('preview')} 
                                className={`rounded-xl h-9 px-6 font-black text-[10px] uppercase tracking-widest ${activeTab === 'preview' ? 'shadow-xl shadow-primary/20' : ''}`}
                            >
                                <Eye className="w-3.5 h-3.5 mr-2" /> Live Simulator
                            </Button>
                        </div>

                        {/* ── COMPOSER TAB ─────────────────────────────────────── */}
                        {activeTab === 'edit' && (
                            <div className="flex-1 overflow-y-auto p-10 space-y-10 bg-background">
                                <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    
                                    {/* Subject Construction */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between px-2">
                                            <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Strategic Subject Line</h4>
                                            <Badge variant="secondary" className="bg-muted border-none font-bold text-[9px]">{currentSubject?.length || 0} Chars</Badge>
                                        </div>
                                        <div 
                                            className={`relative transition-all duration-300 ${isDraggingOver === 'subject' ? 'scale-[1.02] shadow-2xl ring-4 ring-primary/20 rounded-2xl' : ''}`}
                                            onDragOver={(e) => { e.preventDefault(); setIsDraggingOver('subject'); }}
                                            onDragLeave={() => setIsDraggingOver(null)}
                                            onDrop={(e) => { e.preventDefault(); setIsDraggingOver(null); const text = e.dataTransfer.getData('text/plain'); if (text) insertIntoSubject(text); }}
                                        >
                                            <input
                                                ref={subjectRef}
                                                type="text"
                                                placeholder="Write a compelling subject hook..."
                                                className="w-full text-lg font-bold border-2 border-border/60 bg-background rounded-2xl p-5 outline-none focus:border-primary transition-all shadow-inner"
                                                value={currentSubject || ''}
                                                onChange={(e) => handleContentChange('subjectTemplate', e.target.value)}
                                            />
                                            {isDraggingOver === 'subject' && (
                                                <div className="absolute inset-0 rounded-2xl bg-primary/10 border-2 border-dashed border-primary flex items-center justify-center pointer-events-none animate-pulse">
                                                    <span className="text-xs font-black uppercase tracking-widest text-primary bg-background px-4 py-2 rounded-full border shadow-lg">Drop Variable</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Body Editor Construction */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between px-2">
                                            <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Rich Messaging Surface</h4>
                                            <div className="flex gap-2">
                                                <Badge variant="outline" className="text-[9px] font-black border-border/40">HTML5 ENABLED</Badge>
                                                <Badge variant="outline" className="text-[9px] font-black border-border/40">RTL AUTO</Badge>
                                            </div>
                                        </div>
                                        <div
                                            className={`h-[450px] transition-all duration-300 ${isDraggingOver === 'body' ? 'scale-[1.01] shadow-2xl ring-4 ring-primary/20 rounded-3xl' : ''}`}
                                            onDragOver={(e) => { e.preventDefault(); setIsDraggingOver('body'); }}
                                            onDragLeave={() => setIsDraggingOver(null)}
                                            onDrop={(e) => { e.preventDefault(); setIsDraggingOver(null); const text = e.dataTransfer.getData('text/plain'); if (text) insertIntoBody(text); }}
                                        >
                                            <NativeRichEditor
                                                value={currentBody || ''}
                                                onChange={(v) => handleContentChange('bodyTemplate', v)}
                                                onImageUpload={handleImageUpload}
                                                isUploading={isUploading}
                                            />
                                        </div>
                                    </div>

                                    {/* Assets & Attachments */}
                                    <div className="pt-6 border-t border-border/60">
                                        <div className="bg-muted/30 border-2 border-dashed border-border/60 rounded-[2.5rem] p-8">
                                            <div className="flex items-center justify-between mb-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-background border flex items-center justify-center text-primary shadow-sm">
                                                        <FileText className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-black uppercase tracking-tight">Campaign Assets</h4>
                                                        <p className="text-[10px] text-muted-foreground font-medium">Attach PDFs, Sales Decks, or Case Studies.</p>
                                                    </div>
                                                </div>
                                                <div className="relative">
                                                    <input type="file" onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" disabled={isUploading} />
                                                    <Button variant="secondary" className="rounded-xl h-10 px-6 font-black text-[10px] uppercase tracking-widest bg-background border shadow-sm">
                                                        {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                                        Upload Resource
                                                    </Button>
                                                </div>
                                            </div>

                                            {(localData.attachments || []).length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {localData.attachments.map((att, idx) => (
                                                        <div key={idx} className="flex items-center justify-between p-4 bg-background border-2 border-border/40 rounded-2xl group shadow-sm hover:border-primary/30 transition-all">
                                                            <div className="flex items-center gap-4 overflow-hidden">
                                                                <FileText className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                                                <div className="truncate">
                                                                    <p className="text-xs font-black truncate">{att.filename}</p>
                                                                    <p className="text-[9px] text-muted-foreground font-bold tracking-widest uppercase">{(att.size / 1024).toFixed(0)} KB • {att.contentType.split('/')[1]}</p>
                                                                </div>
                                                            </div>
                                                            <button onClick={() => removeAttachment(idx)} className="p-2 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/5 rounded-lg transition-all active:scale-90">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="py-10 text-center flex flex-col items-center opacity-30 grayscale">
                                                    <UploadCloud className="w-10 h-10 mb-2" />
                                                    <p className="text-[10px] font-black uppercase tracking-widest">No resources linked to this node</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── SIMULATOR TAB ────────────────────────────────────── */}
                        {activeTab === 'preview' && (
                            <div className="flex-1 overflow-y-auto p-12 bg-muted/20 flex flex-col items-center">
                                <div className="w-full max-w-4xl space-y-8 animate-in zoom-in-95 duration-500">
                                    <div className="bg-background rounded-[3rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)] border border-border/40 overflow-hidden relative">
                                        {/* Browser Chrome */}
                                        <div className="border-b border-border/30 px-10 py-8 bg-muted/20">
                                            <div className="flex gap-1.5 mb-6">
                                                <div className="w-3 h-3 rounded-full bg-rose-500/50" />
                                                <div className="w-3 h-3 rounded-full bg-amber-500/50" />
                                                <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                                            </div>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 w-12 text-right">From</span>
                                                    <div className="text-xs font-bold bg-muted/50 px-3 py-1.5 rounded-full border border-border/40">Marketing Suite <span className="font-medium text-muted-foreground">&lt;outreach@workspace.mailwise.ai&gt;</span></div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 w-12 text-right">To</span>
                                                    <div className="text-xs font-bold bg-muted/50 px-3 py-1.5 rounded-full border border-border/40">Sample Prospect <span className="font-medium text-muted-foreground">&lt;jane.doe@acme.inc&gt;</span></div>
                                                </div>
                                                <div className="flex items-start gap-4 pt-4 border-t border-border/40">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 w-12 text-right mt-1">Subject</span>
                                                    <h3 className="text-xl font-black tracking-tight text-foreground truncate pl-1">
                                                        {previewing ? '...' : (previewData.resolvedSubject || 'Drafting Strategy...')}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Body Content */}
                                        <div className="p-12 min-h-[400px]">
                                            {previewing ? (
                                                <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-40">
                                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                                    <p className="text-xs font-black uppercase tracking-widest">Compiling Simulation...</p>
                                                </div>
                                            ) : previewData.resolvedBodyHtml ? (
                                                <div className="prose prose-zinc prose-lg max-w-none text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: previewData.resolvedBodyHtml }} />
                                            ) : (
                                                <div className="text-center py-20 opacity-30 italic font-bold">Waiting for content to render.</div>
                                            )}
                                        </div>

                                        {/* Simulator Footer */}
                                        {previewData.attachments?.length > 0 && (
                                            <div className="bg-muted/5 border-t border-border/30 p-8 flex flex-col gap-4">
                                                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">Attached Strategy Assets ({previewData.attachments.length})</h4>
                                                <div className="flex flex-wrap gap-3">
                                                    {previewData.attachments.map((att, i) => (
                                                        <div key={i} className="flex items-center gap-3 bg-white border-2 border-border/40 px-5 py-3 rounded-2xl text-xs font-bold shadow-sm whitespace-nowrap">
                                                            <FileText className="w-4 h-4 text-primary" />
                                                            {att.filename}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="bg-primary/5 border-2 border-primary/10 rounded-[2rem] p-8 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center border border-emerald-500/20">
                                                <Sparkles className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h5 className="text-xs font-black uppercase tracking-tight">Strategic Real-time Resolution</h5>
                                                <p className="text-[10px] text-muted-foreground font-medium italic">What you see is exactly what the prospect will receive.</p>
                                            </div>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => handlePreview()}
                                            disabled={previewing}
                                            className="rounded-xl h-10 px-5 font-black text-[10px] uppercase tracking-widest hover:bg-white"
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${previewing ? 'animate-spin' : ''}`} /> Force Re-Simulate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showTemplatePicker && (
                <TemplatePickerModal
                    workspaceId={workspaceId}
                    onClose={() => setShowTemplatePicker(false)}
                    onSelect={handleApplyTemplate}
                />
            )}
        </div>
    );
};

export default SequenceEmailEditorDrawer;
